import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useSupportedCountries } from '../lib/hooks/useSupportedCountries';
import { useGeolocation } from '../lib/hooks/useGeolocation';

export interface CountryOption {
  code: string;
  name: string;
  flag: string;
  region: string;
}

interface SearchableCountrySelectorProps {
  value?: string;
  onChange: (countryCode: string, country: CountryOption) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  priorityCountries?: string[];
  enableGeolocation?: boolean; // Enable IP-based country detection
  showGeolocationSuggestion?: boolean; // Show detected country as suggestion
}

const REGION_ORDER = ['Africa', 'Europe', 'Asia', 'North America', 'South America', 'Oceania', 'Other'];
const REGION_ICONS: Record<string, string> = {
  'Africa': '🌍',
  'Europe': '🏰',
  'Asia': '🏯',
  'North America': '🗽',
  'South America': '🌴',
  'Oceania': '🏝️',
  'Other': '🌐'
};

export const SearchableCountrySelector: React.FC<SearchableCountrySelectorProps> = ({
  value,
  onChange,
  placeholder = 'Choose your country...',
  error,
  disabled = false,
  className = '',
  priorityCountries = ['ZAF', 'NAM', 'SWZ', 'BWA', 'GBR', 'USA'],
  enableGeolocation = true,
  showGeolocationSuggestion = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { countries, loading } = useSupportedCountries();
  const { detectedCountry, loading: geoLoading } = useGeolocation(enableGeolocation);

  // Map database countries to CountryOption
  const countryOptions: CountryOption[] = useMemo(() => {
    return countries.map(c => ({
      code: c.country_code,
      name: c.country_name,
      flag: c.flag_emoji || '🏳️',
      region: c.region || 'Other'
    }));
  }, [countries]);

  // Selected country data
  const selectedCountry = useMemo(() => {
    return countryOptions.find(c => c.code === value);
  }, [countryOptions, value]);

  // Get detected country option for suggestion
  const detectedCountryOption = useMemo(() => {
    if (!detectedCountry || !showGeolocationSuggestion) return null;
    return countryOptions.find(c => c.code === detectedCountry.countryCode3);
  }, [countryOptions, detectedCountry, showGeolocationSuggestion]);

  // Show suggestion only when no value is selected and we have detected country
  const showSuggestion = !value && detectedCountryOption && !suggestionDismissed && !geoLoading;

  // Handle accepting the geolocation suggestion
  const handleAcceptSuggestion = () => {
    if (detectedCountryOption) {
      onChange(detectedCountryOption.code, detectedCountryOption);
    }
  };

  // Common search aliases for countries (maps search terms to country codes)
  const COUNTRY_ALIASES: Record<string, string[]> = {
    'america': ['USA'],
    'us': ['USA'],
    'united states of america': ['USA'],
    'usa': ['USA'],
    'uk': ['GBR'],
    'england': ['GBR'],
    'britain': ['GBR'],
    'great britain': ['GBR'],
    'south africa': ['ZAF'],
    'sa': ['ZAF'],
    'mzansi': ['ZAF'],
    'holland': ['NLD'],
    'korea': ['KOR', 'PRK'],
    'uae': ['ARE'],
    'emirates': ['ARE'],
    'ivory coast': ['CIV'],
    'czech': ['CZE'],
    'czechia': ['CZE'],
    'congo': ['COD', 'COG'],
    'russia': ['RUS'],
    'taiwan': ['TWN'],
    'eswatini': ['SWZ'],
    'swaziland': ['SWZ'],
  };

  // Filter and group countries
  const { priorityList, groupedCountries, flatList } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    // Filter by search query
    let filtered = countryOptions;
    if (query) {
      // Find alias matches
      const aliasMatches = new Set<string>();
      for (const [alias, codes] of Object.entries(COUNTRY_ALIASES)) {
        if (alias.includes(query) || query.includes(alias)) {
          codes.forEach(code => aliasMatches.add(code));
        }
      }

      filtered = countryOptions.filter(c =>
        c.name.toLowerCase().includes(query) ||
        c.code.toLowerCase().includes(query) ||
        c.region.toLowerCase().includes(query) ||
        aliasMatches.has(c.code)
      );
    }

    // Separate priority countries ONLY when not searching
    // When searching, show ALL matches in regional groups (fixes USA/GBR/ZAF disappearing on search)
    const priority = query ? [] : filtered.filter(c => priorityCountries.includes(c.code));
    const rest = query ? filtered : filtered.filter(c => !priorityCountries.includes(c.code));

    // Group remaining by region
    const grouped: Record<string, CountryOption[]> = {};
    for (const country of rest) {
      if (!grouped[country.region]) {
        grouped[country.region] = [];
      }
      grouped[country.region].push(country);
    }

    // Sort each region alphabetically
    for (const region of Object.keys(grouped)) {
      grouped[region].sort((a, b) => a.name.localeCompare(b.name));
    }

    // Build flat list for keyboard navigation
    const flat: CountryOption[] = [...priority];
    for (const region of REGION_ORDER) {
      if (grouped[region]) {
        flat.push(...grouped[region]);
      }
    }

    return { priorityList: priority, groupedCountries: grouped, flatList: flat };
  }, [countryOptions, searchQuery, priorityCountries]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-country-item]');
      items[focusedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex]);

  const handleSelect = (country: CountryOption) => {
    onChange(country.code, country);
    setIsOpen(false);
    setSearchQuery('');
    setFocusedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => Math.min(prev + 1, flatList.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && flatList[focusedIndex]) {
          handleSelect(flatList[focusedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchQuery('');
        break;
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Selected Display / Trigger */}
      <button
        type="button"
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`w-full px-4 py-4 bg-gray-800/60 border rounded-xl focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500/50 text-white text-lg backdrop-blur-sm transition-all duration-200 text-left flex items-center justify-between ${
          error ? 'border-red-500/50' : 'border-gray-600/50'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800/80 cursor-pointer'}`}
      >
        <div className="flex items-center space-x-3">
          {selectedCountry ? (
            <>
              <span className="text-2xl">{selectedCountry.flag}</span>
              <div>
                <span className="font-medium">{selectedCountry.name}</span>
                <span className="text-white ml-2">({selectedCountry.code})</span>
              </div>
            </>
          ) : (
            <span className="text-white">{loading ? 'Loading countries...' : placeholder}</span>
          )}
        </div>
        <svg className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {error && <p className="mt-1 text-sm text-red-400">{error}</p>}

      {/* Geolocation Suggestion Banner */}
      {showSuggestion && detectedCountryOption && (
        <div className="mt-2 p-3 bg-gradient-to-r from-blue-900/40 to-blue-800/30 border border-blue-500/30 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-xl">📍</span>
              <div className="text-sm">
                <span className="text-blue-300">Detected: </span>
                <span className="text-white font-medium">{detectedCountryOption.flag} {detectedCountryOption.name}</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={handleAcceptSuggestion}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Confirm
              </button>
              <button
                type="button"
                onClick={() => setSuggestionDismissed(true)}
                className="px-2 py-1 text-gray-400 hover:text-white text-sm transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
          <p className="text-sm text-blue-400/70 mt-1">Click confirm or select a different country below</p>
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-700 sticky top-0 bg-gray-900 z-10">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={inputRef}
                type="search"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={true}
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
                name={`country-search-${Date.now()}`}
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setFocusedIndex(0); }}
                onKeyDown={handleKeyDown}
                placeholder="Type to search countries..."
                className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500/50"
                autoFocus
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-2">💡 Search by country name or code</p>
          </div>

          {/* Country List */}
          <div ref={listRef} className="max-h-80 overflow-y-auto" onKeyDown={handleKeyDown}>
            {flatList.length === 0 ? (
              <div className="p-4 text-center text-white">
                No countries found for "{searchQuery}"
              </div>
            ) : (
              <>
                {/* Priority Countries */}
                {priorityList.length > 0 && !searchQuery && (
                  <div className="border-b border-gray-700">
                    <div className="px-4 py-2 bg-yellow-500/10 text-yellow-400 text-sm font-semibold uppercase tracking-wider">
                      ⭐ Popular Countries
                    </div>
                    {priorityList.map((country, idx) => (
                      <CountryItem
                        key={country.code}
                        country={country}
                        isSelected={value === country.code}
                        isFocused={focusedIndex === idx}
                        onSelect={handleSelect}
                      />
                    ))}
                  </div>
                )}

                {/* Grouped by Region */}
                {REGION_ORDER.map(region => {
                  const regionCountries = groupedCountries[region];
                  if (!regionCountries || regionCountries.length === 0) return null;

                  return (
                    <div key={region} className="border-b border-gray-700 last:border-b-0">
                      <div className="px-4 py-2 bg-gray-800/50 text-gray-300 text-sm font-semibold uppercase tracking-wider sticky top-0">
                        {REGION_ICONS[region]} {region} ({regionCountries.length})
                      </div>
                      {regionCountries.map((country) => {
                        const idx = flatList.findIndex(c => c.code === country.code);
                        return (
                          <CountryItem
                            key={country.code}
                            country={country}
                            isSelected={value === country.code}
                            isFocused={focusedIndex === idx}
                            onSelect={handleSelect}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Country Item Component
interface CountryItemProps {
  country: CountryOption;
  isSelected: boolean;
  isFocused: boolean;
  onSelect: (country: CountryOption) => void;
}

const CountryItem: React.FC<CountryItemProps> = ({ country, isSelected, isFocused, onSelect }) => (
  <button
    type="button"
    data-country-item
    onClick={() => onSelect(country)}
    className={`w-full px-4 py-3 text-left transition-colors duration-100 flex items-center justify-between ${
      isSelected ? 'bg-yellow-500/20 border-l-4 border-yellow-500' : 'border-l-4 border-transparent'
    } ${isFocused ? 'bg-gray-700' : 'hover:bg-gray-800'}`}
  >
    <div className="flex items-center space-x-3">
      <span className="text-xl">{country.flag}</span>
      <span className="text-white font-medium">{country.name}</span>
      <span className="text-gray-500 text-sm">({country.code})</span>
    </div>
    {isSelected && <span className="text-yellow-400">✓</span>}
  </button>
);

export default SearchableCountrySelector;

